import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../../components/feature/Layout';
import { groupCompanies, associateCompanies } from '../../../mocks/companies';

const allCompanies = [...groupCompanies, ...associateCompanies].filter(
  (c) => c.slug !== 'terminal'
);

const SECTION_PADDING = "py-[clamp(60px,8vw,120px)]";
const FONT_STACK = "'Inter', 'Josefin Sans', 'Zen Kaku Gothic New', sans-serif";

const trimPunctuation = (text: string) => {
  if (!text) return '';
  // 改行コードは維持しつつ、末尾の句読点だけを消すように念のため徹底
  return text.toString().replace(/[。\.]$/, ''); 
};

const companyColorMap: Record<string, string> = {
  'swell-holdings': '#000000',
  'swell-partners': '#4682b4',
  'swell-coffee': '#d4dcd6',
  'riceball': '#d3cbc6',
  'rice-ball': '#d3cbc6',
  'meet': '#007bbb',
  'bfp': '#274a78' 
};

function CompanyHero({ company }: { company: any }) {
  const { i18n } = useTranslation();
  const isJa = i18n.language === 'ja';
  const [isLoaded, setIsLoaded] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(true);
  const hasImageBg = ['meet', 'bfp', 'swell-coffee', 'swell-partners'].includes(company.slug);
  const isRiceBall = company.slug === 'riceball' || company.slug === 'rice-ball';
  const isMeet = company.slug === 'meet';
  const isPartners = company.slug === 'swell-partners';

  useEffect(() => {
    setIsLoaded(false);
    const timer = setTimeout(() => setIsLoaded(true), 50);
    return () => clearTimeout(timer);
  }, [company.slug]);

  useEffect(() => {
    const handleScroll = () => { setShowScrollBtn(window.scrollY <= 40); };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const customSmoothScroll = (targetY: number, duration: number) => {
    const html = document.documentElement;
    const originalStyle = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto'; 
    const startY = window.pageYOffset;
    const HEADER_OFFSET = 80;
    const finalTargetY = targetY - HEADER_OFFSET;
    const difference = finalTargetY - startY;
    let startTime: number | null = null;
    const step = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = currentTime - startTime;
      const percent = Math.min(progress / duration, 1);
      const easeInOutCubic = percent < 0.5 ? 4 * percent * percent * percent : 1 - Math.pow(-2 * percent + 2, 3) / 2;
      window.scrollTo(0, startY + difference * easeInOutCubic);
      if (progress < duration) { window.requestAnimationFrame(step); } else { html.style.scrollBehavior = originalStyle; }
    };
    window.requestAnimationFrame(step);
  };

  const imageBgUrl: Record<string, string> = {
    'swell-coffee': 'url(https://static.readdy.ai/image/f7124df3158b8e15d4b92ff14b495f5f/c5ba8396887e80174f7b50e62b633b4b.jpeg)',
    'meet': 'url(https://static.readdy.ai/image/f7124df3158b8e15d4b92ff14b495f5f/3427e2a144ee4dbc6877e4a813fd0fa2.png)',
    'bfp': 'url(https://static.readdy.ai/image/f7124df3158b8e15d4b92ff14b495f5f/86a61ac2042e067dae5cca6cf83b429c.jpeg)', 'swell-partners': 'url(/logo/live.png)',
  };

  return (
    <section 
      style={{ 
        backgroundColor: company.slug === 'swell-holdings' ? '#a2d7dd' : (hasImageBg ? '#000' : (company.bgColor || '#ffffff')) 
      }} 
      className={`relative overflow-hidden w-full h-[550px] md:h-[630px] flex items-end pb-[60px] md:pb-[100px] transition-opacity duration-700 ease-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
    >
      <style>{`
        @keyframes heroReveal {
          0% { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .hero-reveal { animation: heroReveal 1.2s cubic-bezier(0.16, 1, 0.3, 1) both; }
        
        .fade-target {
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) !important;
        }

        @keyframes slowDeepHeartbeat {
          0% { transform: scale(1); filter: brightness(0.8); }
          15% { transform: scale(1.015); filter: brightness(1.1); }
          30% { transform: scale(1); filter: brightness(0.8); }
          45% { transform: scale(1.015); filter: brightness(1.1); }
          70% { transform: scale(1); filter: brightness(0.8); }
          100% { transform: scale(1); filter: brightness(0.8); }
        }
        .animate-heartbeat { 
          animation: slowDeepHeartbeat 6s ease-in-out infinite; 
        }
      `}</style>

      {hasImageBg && (
        <>
          <div 
            className={`absolute inset-0 bg-cover bg-center z-0 transition-all duration-[1200ms] ease-out ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'} ${isPartners ? 'animate-heartbeat' : ''}`} 
            style={{ backgroundImage: imageBgUrl[company.slug] }} 
          />
          <div className={`absolute inset-0 bg-black/40 z-0 transition-opacity duration-[1200ms] ${isLoaded ? 'opacity-100' : 'opacity-0'}`} />
        </>
      )}

      {company.slug === 'swell-holdings' && (
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <img 
            src="/logo/s.png" 
            alt="" 
            className={`absolute top-1/2 right-0 -translate-y-1/2 translate-x-[20%] h-[130%] w-auto object-contain transition-all duration-[1500ms] ease-out ${isLoaded ? 'opacity-[0.15] scale-100' : 'opacity-0 scale-105'}`} 
            style={{ 
              filter: 'brightness(0) invert(1)',
              maxWidth: 'none',
            }} 
          />
        </div>
      )}

      {isRiceBall && (
        <div className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-all duration-[1500ms] ease-out select-none z-0 ${isLoaded ? 'opacity-[0.03] scale-100' : 'opacity-0 scale-105'}`}>
          <img src={company.logo} alt="" className="w-[120%] max-w-none object-contain" />
        </div>
      )}

      <div className={`absolute top-6 left-1/2 -translate-x-1/2 z-20 transition-all duration-500 ${showScrollBtn ? 'opacity-100' : 'opacity-0'}`}>
        <button onClick={() => { const f = document.querySelector('footer'); if(f) customSmoothScroll(f.offsetTop, 2500); }} className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-300/60 hover:bg-gray-300/90 text-gray-500 cursor-pointer">
          <i className="ri-arrow-down-s-line ri-lg"></i>
        </button>
      </div>

      <div className="absolute top-[50px] md:top-[80px] left-0 right-0 z-20 pointer-events-none">
        <div className="site-container flex items-center gap-3 text-[10px] tracking-[0.3em] font-medium pointer-events-auto">
          <Link to="/" className={hasImageBg ? 'text-white/40 hover:text-white' : 'text-gray-400 hover:text-gray-900'}>HOME</Link>
          <span className={hasImageBg ? 'text-white/20' : 'text-gray-300'}>/</span>
          <span className={`font-bold uppercase ${hasImageBg ? 'text-white/80' : 'text-gray-600'}`}>{company.name}</span>
        </div>
      </div>

      <div className={`site-container relative z-10 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
        <div className="mb-20 md:mb-44 relative">
        <img 
  src={company.slug === 'swell-holdings' ? "/logo/sh.png" : company.logo} 
  alt="" 
  className={`w-auto transition-all duration-700 ${
    isLoaded ? 'opacity-100' : 'opacity-0'
  } ${
    ['swell-holdings', 'swell-partners'].includes(company.slug)
      ? '-translate-y-20 -translate-x-4 md:translate-y-[10px] md:translate-x-0' 
    : company.slug === 'bfp'
      ? '-translate-y-[20px] md:translate-y-[10px]' 
    : '-translate-y-5 md:translate-y-9'
  } ${
    ['bfp', 'swell-holdings', 'swell-partners'].includes(company.slug)
      ? 'max-h-[85px] md:max-h-[110px] max-w-[238px] md:max-w-[272px]' 
      : 'max-h-[100px] md:max-h-[130px] max-w-[280px] md:max-w-[320px]'
  } object-contain object-left`}
/>
          {isPartners && (
            <div 
              key={`partners-catch-${i18n.language}`}
              className={`absolute top-[135%] md:top-[150%] -translate-y-1/2 right-2 text-white uppercase font-black text-right whitespace-pre-line hero-reveal fade-target ${isLoaded ? 'opacity-90' : 'opacity-0'}`}
              style={{ 
                fontSize: isJa ? "clamp(14px, 4.1vw, 40px)" : "clamp(18px, 3.5vw, 34px)",
                fontFamily: '"Plus Jakarta Sans", sans-serif', 
                lineHeight: '1.3', 
                width: 'max-content', 
                letterSpacing: isJa ? '-0.02em' : '-0.01em' 
              }}
            >
              {isJa ? "私達はエンタテインメントに関わる\n全ての⼈の価値を⾼め⼈⽣を豊かにします"
                   : "WE ENHANCE VALUE FOR EVERYONE\nINVOLVED IN ENTERTAINMENT, ENRICHING LIVES"}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 relative items-end">
          <div className="lg:col-span-8 flex flex-col items-start min-w-0">
            <h1 
              className={`font-bold tracking-tight leading-[1.05] hero-reveal mb-0 relative top-[30px] ${hasImageBg ? 'text-white' : 'text-gray-900'} ${company.slug === 'bfp' ? 'whitespace-normal text-[28px] md:text-[48px] md:whitespace-nowrap' : 'text-[clamp(24px,6vw,48px)] whitespace-nowrap'}`} 
              style={{ fontFamily: "'Josefin Sans', sans-serif" }}
            >
              {company.slug === 'bfp' ? (
                <>
                  <span className="md:hidden">BASEMENT FACTORY<br />PRODUCTIONS</span>
                  <span className="hidden md:inline">{company.name}</span>
                </>
              ) : (
                company.name
              )}
            </h1>
            <p key={`tagline-${i18n.language}`} className={`mt-8 text-[clamp(16px,3.8vw,22px)] leading-relaxed tracking-[0.05em] font-medium max-w-2xl hero-reveal fade-target ${hasImageBg ? 'text-white/70' : 'text-gray-500'}`}>
              {trimPunctuation(company.tagline)}
            </p>
          </div>

          <div className="lg:col-span-4 flex flex-col items-start lg:items-end relative">
            {isMeet && (
              <div 
                key={`meet-catch-${i18n.language}`} 
                className={`absolute top-[-380%] -translate-y-1/2 right-0 text-white uppercase font-black text-right whitespace-pre-line hero-reveal fade-target ${isLoaded ? 'opacity-90' : 'opacity-0'}`} 
                style={{ 
                  fontSize: "clamp(24px, 4.5vw, 52px)",
                  fontFamily: '"Plus Jakarta Sans", sans-serif', 
                  lineHeight: "1.2", 
                  width: 'max-content',
                  letterSpacing: isJa ? '-0.02em' : '-0.01em' 
                }}
              >
                {isJa ? "ワンタッチが世界を変える" : <>ONE TOUCH{"\n"}CHANGES THE WORLD</>}
              </div>
            )}

            {(company.website || isRiceBall) && company.slug !== 'swell-holdings' && !isPartners && (
              <a href={isRiceBall ? "https://rbp.jp/" : company.website} target="_blank" rel="noopener" className={`group relative inline-flex items-center gap-3 px-8 py-4 text-[11px] tracking-[0.25em] font-bold rounded-sm overflow-hidden transition-all duration-300 hero-reveal ${hasImageBg ? 'bg-transparent text-white border border-white/40 hover:text-gray-900' : 'bg-transparent text-gray-900 border border-gray-900/20 hover:text-white'}`}>
                <span className={`absolute inset-0 z-0 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-out ${hasImageBg ? 'bg-white' : 'bg-gray-900'}`} />
                <span className="relative z-10 flex items-center gap-3">OFFICIAL WEBSITE<i className="ri-arrow-right-up-line transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" /></span>
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutSection({ company }: { company: any }) {
  const { i18n } = useTranslation();
  const isJa = i18n.language === 'ja';
  const text = isJa ? (company.description_ja || company.description) : company.description;
  return (
    <section className="bg-white">
      <div className={`site-container ${SECTION_PADDING}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            <p className="text-[11px] tracking-[0.25em] text-gray-400 font-medium uppercase border-l-2 lg:border-l-0 lg:border-t-2 border-gray-100 pl-4 lg:pl-0 lg:pt-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              About
            </p>
          </div>
          <div className="lg:col-span-9">
            <p key={`about-${i18n.language}`} className={`leading-[2.0] text-gray-800 max-w-5xl whitespace-pre-wrap tracking-wider font-medium text-left hero-reveal fade-target ${company.slug === 'swell-holdings' && isJa ? 'text-[clamp(12px,3.4vw,20px)]' : isJa ? 'text-[12px] lg:text-[20px]' : 'text-[clamp(16px,4vw,24px)]'}`}>
              {trimPunctuation(text)}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function BusinessSection({ company }: { company: any }) {
  const { i18n } = useTranslation();
  const items = i18n.language === 'ja' ? (company.businessOverview_ja || company.businessOverview) : company.businessOverview;
  if (!items || items.length === 0) return null;

  const effectColor = companyColorMap[company.slug] || '#4682b4';

  return (
    <section className="bg-[#FAFAF8] border-t border-gray-100" style={{ fontFamily: FONT_STACK }}>
      <div className={`site-container ${SECTION_PADDING}`}>
        <div className="mb-12 text-center lg:text-left">
          <p className="text-[11px] tracking-[0.25em] text-gray-400 font-medium mb-4 uppercase">What we do</p>
          <h2 className="text-[clamp(26px,3.5vw,40px)] font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>Business Overview</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((item: any, i: number) => (
            <div 
              key={`${i}-${i18n.language}`} 
              className="group relative bg-white rounded-xl p-8 border border-gray-100 overflow-hidden transition-all duration-400 hover:border-transparent shadow-sm cursor-pointer"
            >
              <div 
                className="absolute inset-0 w-[150%] h-full z-0 transition-all duration-[900ms] ease-in-out skew-x-[69deg] -translate-x-[160%] opacity-0 group-hover:translate-x-[-45%] group-hover:opacity-100"
                style={{ backgroundColor: effectColor }}
              />
              <div 
                className="absolute inset-0 w-[150%] h-full z-0 transition-all duration-[900ms] ease-in-out skew-x-[69deg] translate-x-[160%] opacity-0 group-hover:translate-x-[35%] group-hover:opacity-100"
                style={{ backgroundColor: effectColor }}
              />
              
              <div className="relative z-10 transition-colors duration-400 group-hover:text-white">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-50 text-gray-300 mb-8 text-[14px] font-black group-hover:bg-white/20 group-hover:text-white transition-all duration-400">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3 className="text-[18px] font-bold text-gray-900 mb-5 tracking-tight group-hover:text-white transition-colors duration-400 hero-reveal fade-target">
                  {item.title}
                </h3>
                <p className="text-[14px] leading-[1.9] text-gray-500 font-medium tracking-wide group-hover:text-white/90 transition-colors duration-400 hero-reveal fade-target">
                  {trimPunctuation(item.desc)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function InfoSection({ company }: { company: any }) {
  const { i18n } = useTranslation();
  const isJa = i18n.language === 'ja';
  if (!company.info || Object.keys(company.info).length === 0) return null;
  
  const labelMap: Record<string, string> = { 
    established: 'Established', 
    location: 'Address', 
    business: 'Business', 
    representative: 'Representative', 
    capital: 'Capital', 
    mainBank: 'Main Bank' 
  };

  const displayEntries = Object.entries(company.info).filter(([key, value]) => {
    if (!value || value === '-') return false;
    const isJaKey = key.endsWith('_ja');
    if (isJa) return isJaKey || !Object.keys(company.info).includes(`${key}_ja`);
    return !isJaKey;
  }).map(([key, value]) => [key.replace('_ja', ''), value]);

  return (
    <section className="bg-white border-t border-gray-100" style={{ fontFamily: FONT_STACK }}>
      <div className={`site-container ${SECTION_PADDING}`}>
        <div className="mb-10">
          <p className="text-[11px] tracking-[0.25em] text-gray-400 font-medium mb-4 uppercase">OVERVIEW</p>
          <h2 className="text-[28px] font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>Company Information</h2>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-12 lg:items-stretch">
          <div className="w-full lg:w-1/2 min-w-0">
            {displayEntries.map(([key, value]) => (
              <div key={key} className="flex flex-col sm:flex-row border-b border-gray-100 py-5 gap-2 sm:gap-4">
                <div className="sm:w-40 shrink-0 text-[12px] tracking-[0.08em] text-gray-400 font-medium uppercase">
                  {labelMap[key] || key}
                </div>
                {/* whitespace-pre-line を付与。trimPunctuationで消えないよう処理 */}
                <div key={`${key}-${i18n.language}`} className="text-[14px] text-gray-700 leading-relaxed break-words hero-reveal fade-target whitespace-pre-line">
                  {trimPunctuation(value as string)}
                </div>
              </div>
            ))}
          </div>

          {company.mapCode && (
            <div className="w-full lg:w-1/2 min-w-0">
              <div className="w-full h-full min-h-[350px] md:min-h-[450px] bg-gray-50 rounded-lg overflow-hidden relative border border-gray-100">
                <div 
                  className="absolute inset-0 [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:border-0" 
                  dangerouslySetInnerHTML={{ __html: company.mapCode }} 
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function NavigationSection({ company }: { company: any }) {
  const { i18n } = useTranslation();
  const currentIndex = allCompanies.findIndex((c) => c.slug === company.slug);
  const prev = currentIndex > 0 ? allCompanies[currentIndex - 1] : null;
  const next = currentIndex < allCompanies.length - 1 ? allCompanies[currentIndex + 1] : null;
  return (
    <section className="bg-[#FAFAF8] border-t border-gray-100" style={{ fontFamily: FONT_STACK }}>
      <div className="site-container py-12">
        <div className="flex flex-row items-center justify-between gap-4">
          {prev ? (
            <Link to={`/company/${prev.slug}`} className="flex items-center gap-4 text-gray-400 hover:text-gray-900 group transition-colors">
              <i className="ri-arrow-left-line text-2xl transition-transform group-hover:-translate-x-2" />
              <div><p className="text-[9px] font-bold tracking-[0.2em] uppercase mb-1">Prev</p><p key={`nav-prev-${i18n.language}`} className="text-[12px] font-bold tracking-tight line-clamp-1 hero-reveal fade-target">{prev.name}</p></div>
            </Link>
          ) : <div className="flex-1" />}
          {next ? (
            <Link to={`/company/${next.slug}`} className="flex items-center gap-4 text-right text-gray-400 hover:text-gray-900 group transition-colors">
              <div><p className="text-[9px] font-bold tracking-[0.2em] uppercase mb-1">Next</p><p key={`nav-next-${i18n.language}`} className="text-[12px] font-bold tracking-tight line-clamp-1 hero-reveal fade-target">{next.name}</p></div>
              <i className="ri-arrow-right-line text-2xl transition-transform group-hover:translate-x-2" />
            </Link>
          ) : <div className="flex-1" />}
        </div>
      </div>
    </section>
  );
}

export default function CompanyPage() {
  const { slug } = useParams<{ slug: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const company = allCompanies.find((c) => c.slug === slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useEffect(() => { if (!company && slug) navigate('/', { replace: true }); }, [company, slug, navigate]);
  if (!company) return null;

  const swellStores = [
    { area: 'NAKAMEGURO', name: 'SWELL COFFEE ROASTERS', img: '/logo/1.jpg', googleMap: 'https://maps.app.goo.gl/ovRvVa7M5mfncfKBA', instagram: 'https://www.instagram.com/swell_coffee_roasters/' },
    { area: 'YOKOHAMA', name: 'SWELL COFFEE ROASTERS lab', img: '/logo/2.jpg', googleMap: 'https://maps.app.goo.gl/UUejGH1tuv3wkmwq9', instagram: 'https://www.instagram.com/swell_coffee_roasters_lab/' },
    { area: 'TODOROKI', name: 'Peak by SWELL COFFEE ROASTERS', img: '/logo/3.jpg', googleMap: 'https://maps.app.goo.gl/yFXouK4oDSwpYpCj8', instagram: 'https://www.instagram.com/peak_swellcoffee_roasters/' },
    { area: 'HIMONYA', name: 'BLOOM.by SWELL COFFEE ROASTER', img: '/logo/4.jpg', googleMap: 'https://maps.app.goo.gl/fsehpJ3m7qPnDPG58', instagram: 'https://www.instagram.com/bloom_swell_coffee/' },
  ];

  return (
    <Layout>
      <CompanyHero company={company} />
      <AboutSection company={company} />
      <BusinessSection company={company} />
      
      {slug === 'swell-coffee' && (
        <section className="bg-white border-t border-gray-100" style={{ fontFamily: FONT_STACK }}>
          <div className={`site-container ${SECTION_PADDING}`}>
            <div className="mb-10 text-left">
              <p className="text-[10px] md:text-[11px] tracking-[0.25em] text-gray-400 font-bold mb-4 uppercase">LOCATIONS</p>
              <h2 className="text-[clamp(24px,4vw,40px)] font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>TAP to SHOPCARD</h2>
            </div>
            <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide lg:grid lg:grid-cols-4 gap-4 md:gap-8 pb-6 -mx-4 px-4 md:mx-0 md:px-0">
              {swellStores.map((store, i) => (
                <div key={i} className="relative group overflow-hidden rounded-2xl aspect-[3/4] shadow-sm bg-gray-100 shrink-0 w-[85vw] sm:w-[calc(50%-1rem)] lg:w-full snap-center">
                  <img src={store.img} alt={store.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center z-10 transition-opacity duration-500 group-hover:opacity-0 px-4">
                    <p key={`store-area-${i}-${i18n.language}`} className="text-[18px] font-black text-white tracking-[0.3em] drop-shadow-lg uppercase mb-3 hero-reveal fade-target">{store.area}</p>
                    <h3 key={`store-name-${i}-${i18n.language}`} className="text-white text-[14px] font-bold tracking-[0.15em] leading-[1.3] drop-shadow-md uppercase hero-reveal fade-target">{store.name}</h3>
                  </div>
                  <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center gap-4 z-20 px-4">
                    <div className="flex flex-col gap-2 w-full max-w-[160px]">
                      <a href={store.googleMap} target="_blank" rel="noopener noreferrer" className="border border-white/40 text-white text-[10px] font-bold py-3 tracking-[0.2em] hover:bg-white hover:text-black transition-all text-center uppercase">VIEW MAP</a>
                      <a href={store.instagram} target="_blank" rel="noopener noreferrer" className="border border-white/40 text-white text-[10px] font-bold py-3 tracking-[0.2em] hover:bg-white hover:text-black transition-all text-center uppercase">INSTAGRAM</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {slug !== 'swell-coffee' && <InfoSection company={company} />}
      <NavigationSection company={company} />
    </Layout>
  );
}
